import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import { Link } from "@tanstack/react-router";
import { ArrowRightIcon } from "lucide-react";

import { useAnalysisHighlights } from "../../api/useAnalysisHighlights";
import { useHighlightsAllowed } from "../../api/useHighlightsAllowed";
import { byBestThenTime, isClipHopeless, isClipReady } from "../../model/clipState";
import { useHighlightTitle } from "../HighlightParts";

const CLIPS = msg({
  message: "{count, plural, one {# clip from this match} other {# clips from this match}}",
});
const MOMENTS = msg({
  message: "{count, plural, one {# moment from this match} other {# moments from this match}}",
});
const WATCH = msg`Watch`;
// Сколько кадров показывает строка.
const POSTERS = 3;

type MatchHighlightsLinkProps = {
  analysisId: number;
};

// Строка в разборе (доска 18ж): кадры готовых клипов, сколько клипов или моментов у матча и
// какие, ведёт в раздел «Хайлайты» сразу к этому матчу. Сами моменты разбор не показывает: их
// собирает ежечасный сбор, раздел — их место (решение 03.11.2026). Моментов нет, ещё ищутся,
// не загрузились или хайлайты закрыты игроку — строки нет: разбор полноценен без неё, а строка
// на месте ожидания только мигнула бы.
export const MatchHighlightsLink = ({ analysisId }: MatchHighlightsLinkProps) => {
  const { i18n } = useLingui();
  const allowed = useHighlightsAllowed();
  const query = useAnalysisHighlights(analysisId);
  const title = useHighlightTitle();

  if (!allowed || query.data === undefined) {
    return null;
  }
  const items = query.data.items.filter((highlight) => !isClipHopeless(highlight));
  const first = items[0];
  if (first === undefined) {
    return null;
  }
  const sorted = [...items].sort(byBestThenTime);
  const ready = sorted.filter(isClipReady);
  const posters = ready
    .map((highlight) => highlight.clip.poster_url)
    .filter((url) => url !== null)
    .slice(0, POSTERS);
  const names = [...new Set(sorted.map(title))].join(", ");
  return (
    <Link
      to="/highlights"
      search={{ match: first.match_id, from: "analysis" }}
      className="flex items-center gap-3.5 rounded-xl border border-border-strong bg-background p-2.5 pr-3.5 text-foreground no-underline transition-colors hover:border-accent"
    >
      {posters.length > 0 && (
        <span aria-hidden="true" className="flex shrink-0 gap-1 max-sm:hidden">
          {posters.map((url) => (
            <img
              key={url}
              src={url}
              alt=""
              loading="lazy"
              className="h-[54px] w-24 rounded-md object-cover"
            />
          ))}
        </span>
      )}
      <span className="flex min-w-0 flex-1 flex-col leading-snug">
        <span className="font-semibold">
          {i18n._({
            ...(ready.length > 0 ? CLIPS : MOMENTS),
            values: { count: ready.length > 0 ? ready.length : items.length },
          })}
        </span>
        <span className="truncate text-[13px] text-subtle">{names}</span>
      </span>
      <span className="flex shrink-0 items-center gap-1.5 font-semibold text-accent">
        {i18n._(WATCH)}
        <ArrowRightIcon aria-hidden="true" className="size-4" />
      </span>
    </Link>
  );
};
